import { addToWishlist, deleteFromWishlist, getWishlist, updateWishlistBtn } from '@components/wishlist';
import {
  execOnLoad,
  formatPrice,
  getA11yTranslations,
  initDiscountAppPricing,
  seedDiscountAppPrices,
  lockPageInteraction,
  updateOmnibus,
} from '@general/helpers';
import breakpoints from 'breakpoints';
import { throttle } from 'lodash';
import Swiper from 'swiper';
import { A11y, Navigation, Pagination } from 'swiper/modules';
import 'swiper/scss';
import 'swiper/scss/navigation';
import 'swiper/scss/pagination';
import './quick-buy.scss';
import { initProductForm } from '@components/product';
import { openMinicart } from '@components/minicart';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import PhotoSwipe from 'photoswipe';
import 'photoswipe/style.css';

if (!window.dAgency) window.dAgency = {};
window.dAgency.initQuickBuy = (context) => {
  const snippetContainer = context;

  if (!snippetContainer) return;

  const priceWrapper = snippetContainer.querySelector('.quick-buy__price');

  const wishlistButton = snippetContainer.querySelector('[data-wishlist-btn]');

  // const BISButton = snippetContainer.querySelector('[data-bis-btn]');

  let swiperGalleryInstance = null;
  let syncDiscountAppPrices = null;
  let lightboxInstance = null;

  const initGallery = () => {
    const gallery = snippetContainer.querySelector('.quick-buy__gallery:not(.hidden) .quick-buy__gallery-medias');
    if (gallery) {
      // Destroy previous instance if exists
      if (swiperGalleryInstance && swiperGalleryInstance.destroy) {
        swiperGalleryInstance.destroy(true, true);
        swiperGalleryInstance = null;
      }

      // Zoom on click, same lightbox as the PDP gallery.
      if (lightboxInstance) {
        lightboxInstance.destroy();
        lightboxInstance = null;
      }

      const icons = window?.dAgency?.icons;
      lightboxInstance = new PhotoSwipeLightbox({
        gallery: gallery,
        children: '.quick-buy__gallery-slide--image',
        pswpModule: PhotoSwipe,
        mainClass: 'pswp--custom',
        arrowPrev: true,
        arrowNext: true,
        zoom: false,
        close: true,
        counter: false,
        bgOpacity: 1,
        showHideAnimationType: 'fade',
        ...(icons
          ? {
              ...(icons.chevronLeft && icons.chevronRight
                ? {
                    arrowPrevSVG: icons.chevronLeft,
                    arrowNextSVG: icons.chevronRight,
                  }
                : {}),
              ...(icons.close ? { closeSVG: icons.close } : {}),
            }
          : {}),
      });

      // Override to read data from <button> instead of <a>
      lightboxInstance.addFilter('domItemData', (itemData, element) => {
        const btn = element.querySelector('button[data-pswp-src]');
        if (btn) {
          itemData.src = btn.dataset.pswpSrc;
          itemData.w = parseInt(btn.dataset.pswpWidth, 10);
          itemData.h = parseInt(btn.dataset.pswpHeight, 10);
          itemData.element = btn;
        }
        return itemData;
      });

      lightboxInstance.on('uiRegister', function () {
        lightboxInstance.pswp.ui.registerElement({
          name: 'bulletsIndicator',
          className: 'pswp__bullets-indicator',
          appendTo: 'wrapper',
          onInit: (el, pswp) => {
            const bullets = [];
            let bullet;
            let prevIndex = -1;

            for (let i = 0; i < pswp.getNumItems(); i++) {
              bullet = document.createElement('div');
              bullet.className = 'pswp__bullet';
              bullet.onclick = (e) => {
                pswp.goTo(bullets.indexOf(e.target));
              };
              el.appendChild(bullet);
              bullets.push(bullet);
            }

            pswp.on('change', () => {
              if (prevIndex >= 0) {
                bullets[prevIndex].classList.remove('pswp__bullet--active');
              }
              bullets[pswp.currIndex].classList.add('pswp__bullet--active');
              prevIndex = pswp.currIndex;
            });
          },
        });
      });

      // Move focus to the next slide button when the lightbox is closing
      lightboxInstance.on('closingAnimationEnd', () => {
        const activeSlide = gallery.querySelector('.quick-buy__gallery-slide--image.swiper-slide-active button');
        activeSlide?.focus();
      });

      lightboxInstance.init();

      const leftArrow = snippetContainer.querySelector(
        '.quick-buy__gallery:not(.hidden) .quick-buy__gallery-arrow--left',
      );
      const rightArrow = snippetContainer.querySelector(
        '.quick-buy__gallery:not(.hidden) .quick-buy__gallery-arrow--right',
      );

      const pagination = snippetContainer.querySelector(
        '.quick-buy__gallery:not(.hidden) .quick-buy__gallery-pagination',
      );

      const slides = gallery.querySelectorAll('.swiper-slide');
      const slidesCount = slides.length;

      const options = {
        loop: slidesCount > 1,
        modules: [
          ...(window.dAgency.translations ? [A11y] : []),
          ...(leftArrow && rightArrow ? [Navigation] : []),
          ...(pagination ? [Pagination] : []),
        ],
        ...(leftArrow && rightArrow
          ? {
              navigation: {
                prevEl: leftArrow,
                nextEl: rightArrow,
              },
            }
          : {}),
        ...(pagination
          ? {
              pagination: {
                el: pagination,
                clickable: true,
              },
            }
          : {}),
        ...getA11yTranslations(),
      };

      if (gallery.dataset.desktopMode != 'carousel') {
        const lgUp = window.matchMedia(`(min-width: ${breakpoints.lg})`);
        const handleSwiper = () => {
          if (lgUp.matches) {
            if (swiperGalleryInstance && swiperGalleryInstance.destroy) swiperGalleryInstance.destroy(true, true);
          } else {
            swiperGalleryInstance = new Swiper(gallery, options);
          }
        };
        handleSwiper();
        lgUp.addEventListener('change', handleSwiper);
      } else {
        swiperGalleryInstance = new Swiper(gallery, options);
      }
    }
  };

  initGallery();

  // Open modal
  requestAnimationFrame(() => {
    snippetContainer.classList.add('open');
    lockPageInteraction(true, snippetContainer);
  });

  // Close and remove modal
  const closeBtn = snippetContainer.querySelector('.modal__close');
  const backdrop = snippetContainer.querySelector('.modal__backdrop');

  const handleEscape = (e) => {
    if (e.key === 'Escape') closeModal();
  };
  document.addEventListener('keydown', handleEscape);

  const closeModal = () => {
    document.removeEventListener('keydown', handleEscape);
    snippetContainer.classList.remove('open');
    lockPageInteraction(false, snippetContainer);

    // Waiting for transition end
    snippetContainer.addEventListener(
      'transitionend',
      () => {
        // Destroy Swiper and the lightbox before removing DOM node
        if (swiperGalleryInstance?.destroy) {
          swiperGalleryInstance.destroy(true, true);
          swiperGalleryInstance = null;
        }
        if (lightboxInstance) {
          lightboxInstance.destroy();
          lightboxInstance = null;
        }
        snippetContainer?.parentNode?.removeChild(snippetContainer);
      },
      { once: true },
    );
  };

  if (closeBtn) closeBtn.addEventListener('click', closeModal);
  if (backdrop) backdrop.addEventListener('click', closeModal);

  const form = snippetContainer.querySelector('.quick-buy__form');
  const galleryWrapper = snippetContainer.querySelector('.quick-buy__gallery-wrapper');

  if (form) {
    const errorWrapper = form.querySelector('.quick-buy__error-wrapper');
    const errorMessage = form.querySelector('.quick-buy__error [data-message]');

    initProductForm({
      form,
      callbacks: {
        afterVariantChange: async ({ variant, product }) => {
          // Ahead of the wishlist lookup below, which awaits an SDK that can
          // stay pending: the price must not depend on it.
          syncDiscountAppPrices?.({ price: variant?.price });

          if (wishlistButton) {
            wishlistButton.dataset.wishlistVariantId = variant?.id;
            wishlistButton.dataset.wishlistPrice = formatPrice({
              value: variant?.price ?? 0,
            });
            wishlistButton.dataset.wishlistCompareAtPrice = variant?.compare_at_price
              ? formatPrice({
                  value: variant?.compare_at_price,
                })
              : '';

            const wishlist = await getWishlist();
            const wishlistProduct = wishlist?.listcontents?.find(
              (addedProduct) => addedProduct.epi == variant.id && addedProduct.empi == product.id,
            );
            wishlistButton.dataset.wishlistStatus = wishlistProduct ? 'active' : 'inactive';
          }
        },
        beforeSubmit: () => {
          const uniqueIdField = form.querySelector('.js-unique-id');
          if (uniqueIdField) {
            uniqueIdField.value = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
          }
        },
        afterSubmit: () => {
          const quickBuyClose = snippetContainer.querySelector('.quick-buy__container.open .modal__close');
          if (quickBuyClose) quickBuyClose.click();
          openMinicart(true);
        },
        errorCallback: (err) => {
          if (errorWrapper && errorMessage && err?.data?.message) {
            errorMessage.innerText = err.data.message;
            requestAnimationFrame(() => {
              errorWrapper.classList.add('shown');
              setTimeout(() => {
                errorWrapper.classList.remove('shown');
              }, 3000);
            });
          }
        },
      },
    });

    if (typeof window?.dAgency?.initCarousel == 'function') window.dAgency.initCarousel(galleryWrapper);

    updateOmnibus(snippetContainer, priceWrapper);
    seedDiscountAppPrices(snippetContainer);
    syncDiscountAppPrices = initDiscountAppPricing(snippetContainer);

    // Update option label on change
    form.addEventListener('change', (e) => {
      if (!e.target.matches('input[type="radio"]')) return;

      const optionWrapper = e.target.closest('.quick-buy__option');
      if (!optionWrapper) return;

      const currentValue = optionWrapper.querySelector('[data-current-value]');
      if (currentValue) {
        currentValue.textContent = e.target.value;
      }
    });
  }

  if (wishlistButton) {
    updateWishlistBtn(wishlistButton);

    const toggleLoading = throttle(() => {
      wishlistButton.classList.toggle('loading');
    }, 500);

    wishlistButton.addEventListener('click', async (e) => {
      console.log('wishlist quick buy');
      e.preventDefault();
      toggleLoading();

      const variantID = wishlistButton?.dataset?.wishlistVariantId;
      const masterID = wishlistButton?.dataset?.wishlistProductId;
      const productURL = wishlistButton?.dataset?.wishlistProductUrl;
      const price = wishlistButton?.dataset?.wishlistPrice;
      const compareAtPrice = wishlistButton?.dataset?.wishlistCompareAtPrice;

      if (wishlistButton?.dataset?.wishlistStatus === 'active') {
        await deleteFromWishlist({ variantID, masterID, productURL, button: wishlistButton });
      } else {
        await addToWishlist({
          variantID,
          masterID,
          productURL,
          compareAtPrice,
          price,
          button: wishlistButton,
        });
      }
      toggleLoading();
    });
  }
};

execOnLoad(window.dAgency.initQuickBuy, '.quick-buy__container');
